import { useEffect, useMemo, useRef, useState } from 'react';
import { findComponentIndex } from './componentMatch';
import { RunMarker } from '../../../types/bom.types';
import {
  Alternative,
  ComponentAlternative,
  ComponentWithHistory,
  ItemStatus,
  SuggestedComponent,
  ValidationResult,
} from './types';
import {
  FindingRef,
  ImprovementRef,
  ValidationEntry,
  buildValidationEntries,
  findingKeyOf,
} from './validationEntries';

export interface UseValidationStepArgs {
  ruleResults: ValidationResult[];
  componentAlternatives: ComponentAlternative[];
  components: ComponentWithHistory[];
  /** Server-persisted verdicts for the current run — seeds local state. */
  serverReviewStates?: Record<string, 'applied' | 'skipped'> | null;
  /** Persists a verdict change (merge; null deletes). Fire-and-forget. */
  onPersistReviewState?: (states: Record<string, 'applied' | 'skipped' | null>) => void;
  onRunValidation: () => Promise<void>;
  /** The persisted validation marker read with the analysis. */
  validationRun?: RunMarker | null;
  /** Waits for a check already running (found RUNNING on arrival). */
  onAwaitValidation?: () => Promise<void>;
  onReplaceComponent: (componentIndex: number, alternative: Alternative) => Promise<void>;
  onRevertComponent: (componentIndex: number, targetVersionIndex: number) => Promise<void>;
  onAddComponent: (component: SuggestedComponent) => Promise<void>;
  onApplySuggestion: (field: string, value: string) => Promise<void>;
  /**
   * View-only mode (the RFQ's end user outside their fill window): every
   * mutator no-ops — including the first-visit auto-run — and the panels hide
   * their action affordances.
   */
  readOnly?: boolean;
}

/**
 * Owns the validation sub-step's behavior: the auto-run on first visit,
 * explicit re-checks, one-click fixes (add / set value / swap), skips and
 * reverts. Fixes apply instantly and are tracked locally — nothing here
 * re-runs the analysis behind the user's back; "changes since last check"
 * counts what an explicit re-check would fold in. "Added" is derived from
 * the BOM itself (plus this session's adds), so it survives a reload.
 */
export const useValidationStep = ({
  ruleResults,
  componentAlternatives,
  components,
  serverReviewStates,
  onPersistReviewState,
  onRunValidation,
  validationRun,
  onAwaitValidation,
  onReplaceComponent,
  onRevertComponent,
  onAddComponent,
  onApplySuggestion,
  readOnly = false,
}: UseValidationStepArgs) => {
  const [isRunning, setIsRunning] = useState(false);
  // When the running check began: the server's marker when resuming one, else now.
  const [runStartedAt, setRunStartedAt] = useState<string | number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<Record<string, ItemStatus>>({});
  const [addedNames, setAddedNames] = useState<Record<string, true>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [changesSinceCheck, setChangesSinceCheck] = useState(0);
  // Failed checks this visit: after the second, the buyer may go on without one.
  const [failures, setFailures] = useState(0);

  const hasRun = ruleResults.length > 0;
  const autoRunFiredRef = useRef(false);

  const track = async (check: () => Promise<void>, startedAt?: string | null) => {
    setRunStartedAt(startedAt || Date.now());
    setIsRunning(true);
    setError(null);
    try {
      await check();
      // A re-check re-grounds everything: fixed findings now pass, so local
      // review state starts clean against the fresh results.
      setStatuses({});
      setAddedNames({});
      setErrors({});
      setChangesSinceCheck(0);
    } catch (err: any) {
      setError(err?.message || 'Validation failed — ask me to re-check in a moment.');
      setFailures((n) => n + 1);
    } finally {
      setIsRunning(false);
    }
  };

  const run = async () => {
    if (readOnly) return;
    await track(onRunValidation);
  };

  // Arrival reads the run marker first, so a refresh never starts a second
  // check: one already running is waited on, a failed one is shown as
  // failed, and only an unchecked BOM auto-runs (once per mount). The host
  // mounts this panel only once the analysis is in hand, so an empty
  // ruleResults here means "unchecked", never "not fetched yet".
  useEffect(() => {
    if (autoRunFiredRef.current) return;
    autoRunFiredRef.current = true;
    const status = validationRun?.status;
    if (status === 'RUNNING' && onAwaitValidation) {
      track(onAwaitValidation, validationRun?.runningSince);
    } else if (status === 'FAILED' && !hasRun && !readOnly) {
      setError(validationRun?.error?.message || 'The last check did not finish.');
      setFailures(1);
    } else if (!hasRun && !readOnly) {
      run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seed local review state from the server-persisted map (reload/device
  // durability). Local always wins on conflict — it is the newer intent; the
  // server map only fills gaps. Idempotent, so poll refetches are harmless.
  useEffect(() => {
    if (!serverReviewStates || Object.keys(serverReviewStates).length === 0) return;
    setStatuses((prev) => ({ ...serverReviewStates, ...prev }));
  }, [serverReviewStates]);

  const entries: ValidationEntry[] = useMemo(
    () => buildValidationEntries(ruleResults, componentAlternatives, components),
    [ruleResults, componentAlternatives, components],
  );

  const isBusy = (key: string) => !!busy[key];
  const errorOf = (key: string) => errors[key];

  const isComponentInBom = (name: string) =>
    !!addedNames[name] || findComponentIndex(components, name) !== -1;

  /**
   * A finding is resolved either explicitly (applied value / skipped) or by
   * derivation — every component it asked for now exists in the BOM.
   */
  const findingStatus = (ref: FindingRef): ItemStatus => {
    const explicit = statuses[ref.key];
    if (explicit) return explicit;
    const comps = ref.result.suggestedComponentsToAdd || [];
    if (comps.length > 0 && comps.every((c) => isComponentInBom(c.name))) return 'applied';
    return 'needs_review';
  };

  const improvementStatus = (ref: ImprovementRef): ItemStatus => statuses[ref.key] || 'needs_review';

  const markStatus = (key: string, status: ItemStatus) => {
    if (readOnly) return;
    setStatuses((prev) => ({ ...prev, [key]: status }));
    // Every explicit verdict is persisted; needs_review (a revert) deletes
    // the server key. Derived "applied" from BOM contents is never persisted
    // — the BOM itself is the durable record for adds.
    onPersistReviewState?.({ [key]: status === 'needs_review' ? null : status });
  };

  /** Per-key busy/error bookkeeping — concurrent actions never stomp each other. */
  const withBusy = async (key: string, action: () => Promise<void>, onDone: () => void) => {
    if (readOnly) return;
    setBusy((prev) => ({ ...prev, [key]: true }));
    setErrors(({ [key]: _cleared, ...rest }) => rest);
    try {
      await action();
      onDone();
      setChangesSinceCheck((n) => n + 1);
    } catch (err: any) {
      setErrors((prev) => ({
        ...prev,
        [key]: err?.message || 'That fix did not go through — nothing was changed.',
      }));
    } finally {
      setBusy(({ [key]: _done, ...rest }) => rest);
    }
  };

  const addComponent = (compKey: string, comp: SuggestedComponent) =>
    withBusy(
      compKey,
      () => onAddComponent({ ...comp, quantity: comp.quantity || 1 }),
      () => setAddedNames((prev) => ({ ...prev, [comp.name]: true })),
    );

  const applyValue = (ref: FindingRef) => {
    const { affectedField, suggestedValue } = ref.result;
    if (!affectedField || !suggestedValue) return;
    return withBusy(
      ref.key,
      () => onApplySuggestion(affectedField, suggestedValue),
      () => markStatus(ref.key, 'applied'),
    );
  };

  const swapAlternative = (ref: ImprovementRef) => {
    if (ref.componentIndex === -1) {
      setErrors((prev) => ({
        ...prev,
        [ref.key]: `I couldn't match "${ref.alt.originalComponentName}" to a BOM row, so I left it untouched.`,
      }));
      return;
    }
    return withBusy(
      ref.key,
      () => onReplaceComponent(ref.componentIndex, ref.alt.alternatives[0]),
      () => markStatus(ref.key, 'applied'),
    );
  };

  const revert = (componentIndex: number, targetVersionIndex: number, key: string, statusKey: string) =>
    withBusy(
      key,
      () => onRevertComponent(componentIndex, targetVersionIndex),
      () => markStatus(statusKey, 'needs_review'),
    );

  const skip = (key: string) => markStatus(key, 'skipped');

  const componentAt = (index: number): ComponentWithHistory | null =>
    index >= 0 && index < components.length ? components[index] : null;

  // Header counts: passed straight from the engine; "to fix" is live — it
  // falls as fixes land, without waiting for a re-check.
  const passResults = useMemo(() => ruleResults.filter((r) => r.status === 'pass'), [ruleResults]);
  const unresolvedCount = useMemo(
    () =>
      ruleResults.reduce((acc, result, index) => {
        if (result.status === 'pass') return acc;
        return findingStatus({ result, key: findingKeyOf(result, index) }) === 'needs_review'
          ? acc + 1
          : acc;
      }, 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ruleResults, statuses, addedNames, components],
  );

  return {
    isRunning,
    runStartedAt,
    error,
    failures,
    dismissError: () => setError(null),
    hasRun,
    readOnly,
    run,
    entries,
    passResults,
    checkedCount: ruleResults.length,
    unresolvedCount,
    changesSinceCheck,
    findingStatus,
    improvementStatus,
    isComponentInBom,
    isBusy,
    errorOf,
    addComponent,
    applyValue,
    swapAlternative,
    revert,
    skip,
    componentAt,
  };
};

export type ValidationStepState = ReturnType<typeof useValidationStep>;

export default useValidationStep;
